import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Warning } from "@phosphor-icons/react";

const UK_PC = /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i;

/** Split a flat "42 High Street, Leeds, LS1 1AA" into { address_line, postcode } for the address-based check. */
export const splitFlatAddress = (flat) => {
  const s = (flat || "").trim();
  if (!s) return { address_line: "", postcode: "" };
  const m = s.match(UK_PC);
  return { address_line: s.split(",")[0].trim(), postcode: m ? m[1].toUpperCase().replace(/\s+/g, " ") : "" };
};

/**
 * Amber warning when the same fault category has already been raised 2+ times at this property in the last 90 days.
 * endpoint: admin `/clients/{cid}/repeat-check` or portal `/client-hub/repeat-check`. linkBase: "" (admin) or "/portal".
 */
export default function RepeatFaultBanner({ endpoint, category, propertyId, addressLine, postcode, linkBase = "", testid = "repeat-fault-banner" }) {
  const [check, setCheck] = useState(null);
  const ready = !!endpoint && !!category && (!!propertyId || (!!addressLine && !!postcode));

  useEffect(() => {
    if (!ready) { setCheck(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      API.get(endpoint, { params: { category, property_id: propertyId || "", address_line: addressLine || "", postcode: postcode || "" } })
        .then(({ data }) => { if (!cancelled) setCheck(data); })
        .catch(() => { if (!cancelled) setCheck(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [endpoint, category, propertyId, addressLine, postcode, ready]);

  if (!check?.warning) return null;
  const where = check.property?.name || check.property?.address_line || "this property";
  const link = (it) => {
    const seg = it.kind === "request" ? (linkBase ? "requests" : "job-requests") : it.kind === "emergency" ? (linkBase ? "emergencies" : "emergency-jobs") : "jobs";
    return `${linkBase}/${seg}/${it.id}`;
  };
  return (
    <div className="border border-amber-400 bg-amber-500/10 rounded-md p-3 flex gap-2.5" data-testid={testid}>
      <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
      <div className="min-w-0 text-sm">
        <p className="font-semibold text-amber-900 dark:text-amber-200" data-testid={`${testid}-title`}>
          Repeat fault — this would be the {check.ordinal} {category} issue at {where} in {check.window_days} days
        </p>
        <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5">Worth checking whether the earlier fixes held before raising another one-off — a permanent repair or a contract visit may be better.</p>
        <ul className="mt-1.5 space-y-0.5" data-testid={`${testid}-items`}>
          {check.items.map((it) => (
            <li key={it.id} className="text-xs flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-muted-foreground">{(it.date || "").slice(0, 10)}</span>
              <span className="uppercase text-[9px] font-semibold px-1 rounded bg-amber-500/15 text-amber-800 dark:text-amber-200">{it.kind}</span>
              <Link to={link(it)} className="hover:underline truncate" target="_blank" rel="noreferrer" data-testid={`${testid}-item-${it.id}`}>{it.reference ? `${it.reference} · ` : ""}{it.title}</Link>
              {it.status && <span className="text-[10px] text-muted-foreground">({String(it.status).replace(/_/g, " ")})</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
